import { PlasticsMoldingTask, PlasticsMoldingTaskStatus } from '../models/plastics-molding-task.model';
import { RotoSchedule } from '../models/roto-schedule.model';
import { removeMoldingTaskIdFromProductLists } from './remove-molding-task-id-from-product-lists.function';

export interface SameProductMoldingTaskHandoff {
    completedTask: PlasticsMoldingTask;
    incomingTask: PlasticsMoldingTask;
    rotoSchedule: RotoSchedule;
}

/** Prepare the same task and schedule changes made by the supervisor completion flow. */
export function prepareSameProductMoldingTaskHandoff(
    rotoSchedule: RotoSchedule,
    productListId: string,
    machineId: string,
    armId: string,
    currentTask: PlasticsMoldingTask,
    nextTask: PlasticsMoldingTask,
    operatorId: string,
    completedOn: Date,
): SameProductMoldingTaskHandoff {
    const productList = rotoSchedule.productLists.find(list => list.id === productListId);
    if (!productList
        || productList.machineId !== machineId
        || productList.armId !== armId
        || productList.moldingTaskIds[0] !== currentTask.id
        || productList.moldingTaskIds[1] !== nextTask.id) {
        throw new Error('The roto schedule changed. Refresh before continuing to the next task.');
    }

    const currentPlacements = rotoSchedule.productLists.reduce((count, list) =>
        count + list.moldingTaskIds.filter(id => id === currentTask.id).length, 0);
    const nextPlacements = rotoSchedule.productLists.reduce((count, list) =>
        count + list.moldingTaskIds.filter(id => id === nextTask.id).length, 0);
    if (currentPlacements !== 1 || nextPlacements !== 1 || currentTask.id === nextTask.id) {
        throw new Error('These tasks appear more than once on the schedule. Ask a supervisor to advance them.');
    }
    if (!currentTask.productId || currentTask.productId !== nextTask.productId) {
        throw new Error('The next task is not the same product. Ask a supervisor to change the setup.');
    }
    if (currentTask.status !== PlasticsMoldingTaskStatus.IN_PROGRESS
        || nextTask.status !== PlasticsMoldingTaskStatus.IN_PROGRESS) {
        throw new Error('One of these tasks is no longer in progress. Refresh the schedule.');
    }
    if (currentTask.totalQuantity <= 0 || currentTask.completedQuantity < currentTask.totalQuantity) {
        throw new Error('Finish recording the current task before continuing.');
    }
    if (nextTask.totalQuantity <= 0 || nextTask.completedQuantity >= nextTask.totalQuantity) {
        throw new Error('The next task has no remaining quantity. Ask a supervisor to review it.');
    }
    if (nextTask.moldId && nextTask.moldId !== currentTask.moldId) {
        throw new Error('The next task has a different mold assigned. Ask a supervisor to review it.');
    }

    return {
        completedTask: {
            ...currentTask,
            status: PlasticsMoldingTaskStatus.Complete,
            completedOn,
        },
        incomingTask: {
            ...nextTask,
            moldId: currentTask.moldId || '',
        },
        rotoSchedule: {
            productLists: removeMoldingTaskIdFromProductLists(rotoSchedule.productLists, currentTask.id),
            lastSavedById: operatorId,
            lastSavedOn: completedOn,
            moldingTaskUpdatedOn: completedOn.getTime(),
        },
    };
}
